import { useCallback, useEffect, useRef, useState } from 'react';
import { Node } from 'reactflow';
import { flowEditorState, FlowHistoryItem } from './flowEditorState';
import { undoInspectorState } from './undoInspector/undoInspectorState';

type UseUndoRedoOptions = {
  maxHistorySize?: number;
  enableShortcuts?: boolean;
};

// Undo a single commitSnapshot: pop its entry and restore the redo branch it
// cleared, so a net-zero typing run leaves no trace. Idempotent per handle.
type RetractSnapshot = () => void;

type UseUndoRedo = (options?: UseUndoRedoOptions) => {
  undo: () => void;
  redo: () => void;
  // Push a point-in-time graph onto the undo stack and return a handle that undoes
  // exactly this push (so the recorder can retract a run that reverted to its start).
  // Only the FlowEditor recorder calls this, with the pre-edit graph.
  commitSnapshot: (item: FlowHistoryItem) => RetractSnapshot;
  canUndo: boolean;
  canRedo: boolean;
};

const DEFAULT_MAX_HISTORY_SIZE = 100;

// Restore a snapshot node's Tiptap content. Only viewport-intersecting nodes have a
// live editor; off-screen nodes re-hydrate from `node.data.tiptap` on next mount, so
// only push into a live editor when one exists. Return a fresh `node.data` so React
// Flow re-renders.
export function restoreNodeContent(node: Node): Node {
  const editor = flowEditorState.nodeIdToEditorMap.get(node.id);
  // `node.data.tiptap` is the whole ProseMirror document; set it wholesale (wrapping
  // it nests a doc in a doc, which the schema silently drops).
  //
  // emitUpdate: false is load-bearing: the default fires onUpdate, a second
  // clientVersion bump the single restore-suppression doesn't cover, so it records as
  // a fresh edit and clears the redo stack. Restoring must not look like a user edit.
  if (editor && node.data?.tiptap) {
    editor.commands.setContent(node.data.tiptap, false);
  }

  return {
    ...node,
    data: { ...node.data },
  };
}

// https://redux.js.org/usage/implementing-undo-history
export const useUndoRedo: UseUndoRedo = ({
  maxHistorySize = DEFAULT_MAX_HISTORY_SIZE,
  enableShortcuts = true,
} = {}) => {
  // Stacks live in refs, not state: committing to state re-rendered the whole canvas
  // per edit. They're only read in undo/redo and to derive canUndo/canRedo.
  const past = useRef<FlowHistoryItem[]>([]);
  const future = useRef<FlowHistoryItem[]>([]);

  // The only render-driving state. The setter no-ops when unchanged, so a burst of
  // edits re-renders nothing after the first.
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const syncCanUndoRedo = useCallback(() => {
    setCanUndo(past.current.length > 0);
    setCanRedo(future.current.length > 0);
  }, []);

  // Mirror the stacks into the inspector store (no-op when the inspector is off).
  // `current` is the graph between the stacks; pass it post-mutation.
  const publishStacks = useCallback((current: FlowHistoryItem) => {
    undoInspectorState.setStacks(past.current, future.current, current);
  }, []);

  const commitSnapshot = useCallback(
    (item: FlowHistoryItem) => {
      // Remember the stacks this push mutates so the handle can restore them verbatim.
      const prevPast = past.current;
      const prevFuture = future.current;

      past.current = [
        ...prevPast.slice(prevPast.length - maxHistorySize + 1, prevPast.length),
        item,
      ];
      // A fresh edit invalidates the redo branch.
      future.current = [];
      syncCanUndoRedo();
      publishStacks(flowEditorState.getCurrentGraph());

      let retracted = false;
      return () => {
        if (retracted) {
          return;
        }
        retracted = true;
        past.current = prevPast;
        future.current = prevFuture;
        syncCanUndoRedo();
        publishStacks(flowEditorState.getCurrentGraph());
      };
    },
    [maxHistorySize, syncCanUndoRedo, publishStacks],
  );

  const undo = useCallback(() => {
    const pastState = past.current[past.current.length - 1];
    if (pastState) {
      // Flush pending text and disarm its debounce BEFORE restoring, or a buffered
      // keystroke's late write lands on the restored graph and desyncs the stacks.
      flowEditorState.flushActiveEditor();
      flowEditorState.cancelActiveEditorDebounce();
      past.current = past.current.slice(0, past.current.length - 1);
      // Stash from the recorder's synchronous baseline, NOT the render-synced mirror:
      // a burst undo fires the next press before React commits this restore, and only
      // the baseline is current by then.
      future.current = [...future.current, flowEditorState.getRecordedGraph()];
      flowEditorState.applyRestoredGraph(pastState);
      syncCanUndoRedo();
      // Publish before logging so the inspector's depth stamp reflects post-restore.
      publishStacks(pastState);
      undoInspectorState.logEvent('undo', null);
    }
  }, [syncCanUndoRedo, publishStacks]);

  const redo = useCallback(() => {
    const futureState = future.current[future.current.length - 1];
    if (futureState) {
      // As undo: flush and disarm pending text so a late debounce can't land on the
      // restored graph and desync the stacks.
      flowEditorState.flushActiveEditor();
      flowEditorState.cancelActiveEditorDebounce();
      future.current = future.current.slice(0, future.current.length - 1);
      past.current = [...past.current, flowEditorState.getRecordedGraph()];
      flowEditorState.applyRestoredGraph(futureState);
      syncCanUndoRedo();
      publishStacks(futureState);
      undoInspectorState.logEvent('redo', null);
    }
  }, [syncCanUndoRedo, publishStacks]);

  useEffect(() => {
    if (!enableShortcuts) {
      return;
    }

    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Z' && (event.ctrlKey || event.metaKey) && event.shiftKey) {
        redo();
      } else if (event.key === 'z' && (event.ctrlKey || event.metaKey)) {
        undo();
      }
    };

    document.addEventListener('keydown', keyDownHandler);

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [undo, redo, enableShortcuts]);

  return {
    undo,
    redo,
    commitSnapshot,
    canUndo,
    canRedo,
  };
};
